import { useEffect, useRef, useState, type KeyboardEvent } from "react";

import * as api from "../../lib/api.js";
import { captureAnalytics } from "../analytics";
import type { SessionResultOut } from "../contract/api";
import { t } from "../contract/strings";
import { CheckIcon, CloseIcon, EditIcon, cx } from "../ui";

const TARGET = "h-icon-button -my-2";

/**
 * The name field shares the app-wide borderless, rounded surface treatment. The caller's
 * typography and sizing stay intact; focus is the shared visible outline.
 */
const NAME_FIELD =
  "max-w-full m-0 px-2 h-auto bg-surface-2 rounded-control border-0 appearance-none " +
  "[field-sizing:content]";

/**
 * Save and Cancel while renaming: as small as their glyph in the row, so the row keeps its
 * height, each with an invisible 44-unit box around it for the tap.
 */
const EDIT_BUTTON =
  "relative inline-flex flex-none items-center justify-center p-0 bg-transparent border-none " +
  "rounded-control cursor-pointer button-transition enabled:hover:opacity-80 " +
  "disabled:opacity-45 disabled:cursor-default " +
  "after:absolute after:left-1/2 after:top-1/2 after:size-11 after:-translate-x-1/2 " +
  "after:-translate-y-1/2 after:content-['']";
const ICON_BUTTON =
  "inline-flex size-icon-button items-center justify-center rounded-squircle bg-transparent " +
  "border-none cursor-pointer button-transition enabled:hover:opacity-80 enabled:active:button-press " +
  "disabled:opacity-45 disabled:cursor-default";

/** An anonymous result speaker's creator-assigned display name (#alias). */
export function AnonymousSpeakerName({
  sessionId,
  name,
  canRename,
  className,
  iconClassName = "size-icon",
  onRenamed,
}: {
  sessionId: string;
  name: string;
  canRename: boolean;
  className?: string;
  /**
   * The pencil glyph's size. The button keeps its 44-unit target whatever this is, so a
   * smaller glyph sits in proportion to smaller name text without shrinking the tap.
   */
  iconClassName?: string;
  onRenamed: (result: SessionResultOut) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saved, setSaved] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const selectOnOpen = useRef(true);
  const field = useRef<HTMLInputElement>(null);
  const pencil = useRef<HTMLButtonElement>(null);
  const saveSeq = useRef(0);
  const current = (saved ?? name).trim();

  useEffect(() => {
    if (editing) {
      field.current?.focus();
      if (selectOnOpen.current) field.current?.select();
    }
  }, [editing]);

  function open() {
    captureAnalytics("dialog_viewed", { dialog: "rename_meeting" });
    setDraft(current);
    setFailed(false);
    selectOnOpen.current = true;
    setEditing(true);
  }

  function close() {
    captureAnalytics("dialog_dismissed", { dialog: "rename_meeting" });
    setFailed(false);
    setEditing(false);
    requestAnimationFrame(() => pencil.current?.focus());
  }

  function save() {
    const typed = draft;
    const next = typed.trim();
    if (!next || next === current) {
      close();
      return;
    }
    const previous = saved;
    const mine = ++saveSeq.current;
    setSaved(next);
    setEditing(false);
    api.renameSpeaker(sessionId, current, next).then(
      (result) => {
        if (saveSeq.current !== mine) return;
        captureAnalytics("result_renamed", { target: "speaker" });
        onRenamed(result);
      },
      () => {
        if (saveSeq.current !== mine) return;
        setSaved(previous);
        setDraft(typed);
        setFailed(true);
        selectOnOpen.current = false;
        setEditing(true);
      },
    );
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter" && !event.nativeEvent.isComposing) {
      event.preventDefault();
      save();
    } else if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      close();
    }
  }

  if (!canRename) return <span className={className}>{current}</span>;

  return (
    <>
      {editing ? (
        // The field's end to the tick is at least the tick to the cross, in ink (about 22
        // and 21); Save to Cancel stays 16 between boxes, the least that keeps each glyph
        // out of the other's touch box.
        <span className="inline-flex min-w-0 max-w-full items-center gap-5">
          {/*
            The field is the name, editable: the same className (family, size, weight,
            tracking, line height, case) and no form-field box -- no padding, no
            background, a hairline underline as the edit cue and the focus ring. It is as
            wide as what is typed (`field-sizing: content`, `size` where that is not
            supported), at least four characters, at most the row.
          */}
          <input
            ref={field}
            type="text"
            aria-label={t("result.speaker_name")}
            value={draft}
            maxLength={80}
            size={Math.max(draft.length, 4)}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={onKeyDown}
            data-editing-name
            className={cx(className, NAME_FIELD)}
            style={{ minWidth: "4ch" }}
          />
          <span className="inline-flex flex-none items-center gap-4">
            <button
              type="button"
              aria-label={t("common.save")}
              onClick={save}
              disabled={draft.trim().length === 0}
              className={cx(EDIT_BUTTON, "text-accent")}
            >
              <CheckIcon className={iconClassName} />
            </button>
            <button
              type="button"
              aria-label={t("common.cancel")}
              onClick={close}
              className={cx(EDIT_BUTTON, "text-ink")}
            >
              <CloseIcon className={iconClassName} />
            </button>
          </span>
        </span>
      ) : (
        <>
          <span onClick={open} className={cx("cursor-text", className)}>
            {current}
          </span>
          <button
            ref={pencil}
            type="button"
            aria-label={t("result.rename_speaker")}
            onClick={open}
            className={cx(ICON_BUTTON, TARGET, "align-middle ml-1 text-accent")}
          >
            <EditIcon className={iconClassName} />
          </button>
        </>
      )}
      {failed ? (
        <span role="alert" className="block basis-full text-note text-danger">
          {t("result.rename_speaker_failed")}
        </span>
      ) : null}
    </>
  );
}
